import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { EnterpriseEventClient } from './enterprise-event-client';
import { EchoEndpoint } from './enterprise-event-client-helper';

const DEFAULT_PROSPECT_ID_TIME = 365;
const DEFAULT_EXTEND_SESSION_TIME = 1 / 72; // 20 minutes
const PROSPECT_ID_COOKIE_NAME = 'prospect__id';
const PROSPECT_SESSION_ID_COOKIE_NAME = 'prospect__session_id';
const DPID_VERSION_COOKIE_NAME = 'eev2_version';
const DPID_VERSION_VALUE = 'eev2.0.1';

export class DigitalProspectClient {
  private isLocalEnv = false;
  // public to allow for unit testing
  public readonly enterpriseEventClient: EnterpriseEventClient;

  constructor(
    readonly cookieDomain: string,
    readonly env: string,
    private readonly localDomainOverride?: string,
    readonly localEnterpriseEventEndpoint?: string
  ) {
    this.isLocalEnv = Boolean(localDomainOverride);
    this.enterpriseEventClient = EnterpriseEventClient.getInstance(
      env,
      localEnterpriseEventEndpoint
    );
  }

  private get cookieDomainOption() {
    return this.localDomainOverride || this.cookieDomain;
  }

  private checkCookie(name: string) {
    const cookieValue = Cookies.get(name);
    return Boolean(cookieValue && cookieValue !== 'undefined');
  }

  private isDPIDVersionCurrent() {
    const version = Cookies.get(DPID_VERSION_COOKIE_NAME);
    return version && version === DPID_VERSION_VALUE;
  }

  public async init() {
    const dpidCookieExists = this.checkCookie(PROSPECT_ID_COOKIE_NAME);
    const prospectSessionCookieExists = this.checkCookie(
      PROSPECT_SESSION_ID_COOKIE_NAME
    );
    const isEEV2VersionCurrent = this.isDPIDVersionCurrent();

    await this.getIpAddress();

    // check prospect__id and prospect__id version
    if (!dpidCookieExists || !isEEV2VersionCurrent) {
      const prospectId = uuidv4();
      Cookies.set(PROSPECT_ID_COOKIE_NAME, prospectId, {
        expires: DEFAULT_PROSPECT_ID_TIME,
        path: '/',
        domain: this.cookieDomainOption,
        secure: !this.isLocalEnv,
        sameSite: 'Lax',
      });
      Cookies.set(DPID_VERSION_COOKIE_NAME, DPID_VERSION_VALUE, {
        expires: DEFAULT_PROSPECT_ID_TIME,
        path: '/',
        domain: this.cookieDomainOption,
        secure: !this.isLocalEnv,
        sameSite: 'Lax',
      });
      const eventPayload = {
        type: 'com.bestegg.dpid.create',
        source: 'frontend-common',
        subject: prospectId,
      };

      this.enterpriseEventClient.dispatchEnterpriseEvent(eventPayload);
    } else {
      this.dpidVisited();
    }

    // check prospect__session_id
    if (!prospectSessionCookieExists) {
      const prospectSessionId = uuidv4();
      Cookies.set(PROSPECT_SESSION_ID_COOKIE_NAME, prospectSessionId, {
        expires: DEFAULT_EXTEND_SESSION_TIME,
        path: '/',
        domain: this.cookieDomainOption,
        secure: !this.isLocalEnv,
        sameSite: 'Lax',
      });

      const dpid = Cookies.get(PROSPECT_ID_COOKIE_NAME);

      // DPID should always exist here, but if it doesn't, don't dispatch a bad event, and just log
      // a warning
      if (dpid) {
        this.enterpriseEventClient.dispatchEnterpriseEvent({
          type: 'com.bestegg.dpid.prospectSessionStart',
          source: 'frontend-common',
          subject: prospectSessionId,
          parentsubject: dpid,
        });
      } else {
        console.warn(
          'DPID not found while attempting to dispatch prospectSessionStart event'
        );
      }
    } else {
      this.extendSession();
    }
  }

  public extendSession(expires: number | Date = DEFAULT_EXTEND_SESSION_TIME) {
    if (
      !this.checkCookie(PROSPECT_ID_COOKIE_NAME) ||
      !this.checkCookie(PROSPECT_SESSION_ID_COOKIE_NAME)
    ) {
      console.warn(
        'Cannot extend digital prospect session, as no previous session exists. Be sure to call DigitalProspectClient.init() before calling this method'
      );
      return;
    }

    Cookies.set(
      PROSPECT_SESSION_ID_COOKIE_NAME,
      Cookies.get(PROSPECT_SESSION_ID_COOKIE_NAME),
      {
        expires,
        domain: this.cookieDomainOption,
        path: '/',
        secure: !this.isLocalEnv,
        sameSite: 'Lax',
      }
    );
  }

  public dpidVisited() {
    if (!this.checkCookie(PROSPECT_ID_COOKIE_NAME)) {
      console.warn('DPID Missing');
      return;
    }

    const dpid = Cookies.get(PROSPECT_ID_COOKIE_NAME);

    const eventPayload = {
      type: 'com.bestegg.dpid.visited',
      source: 'frontend-common',
      subject: dpid,
      parentsubject: dpid,
      parentrequired: false,
    };

    this.enterpriseEventClient.dispatchEnterpriseEvent(eventPayload);
  }

  public async getIpAddress() {
    try {
      const response = await fetch(
        EchoEndpoint[this.env.toUpperCase() || EchoEndpoint.SBX]
      );
      const data = await response.json();

      if (data?.sourceIp) {
        this.enterpriseEventClient.setClientIpAddress(data.sourceIp);
      }
    } catch (error) {
      console.warn('Failed to fetch IP from echo API:', error);
    }
  }
}
